import React, { useState, useEffect } from 'react';
import {
  Drawer, Box, Typography, TextField, Button, IconButton, Stack, Avatar,
  CircularProgress, Alert, Divider, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions, Grid
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import axios from 'axios';
import { useNextGenAuth } from '../context/NextGenAuthContext';
import API_BASE_URL from '../../Config/config';

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat',
  'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh',
  'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh',
  'Uttarakhand', 'West Bengal', 'Andaman and Nicobar Islands', 'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Jammu and Kashmir',
  'Ladakh', 'Lakshadweep', 'Puducherry'
];

const AVATARS = [
  '/user image.webp',
  'https://api.dicebear.com/7.x/avataaars/svg?seed=Felix',
  'https://api.dicebear.com/7.x/avataaars/svg?seed=Aneka',
  'https://api.dicebear.com/7.x/avataaars/svg?seed=Gizmo',
  'https://api.dicebear.com/7.x/avataaars/svg?seed=Milo',
  'https://api.dicebear.com/7.x/avataaars/svg?seed=Luna',
  'https://api.dicebear.com/7.x/avataaars/svg?seed=Simba',
  'https://api.dicebear.com/7.x/avataaars/svg?seed=Nala',
  'https://api.dicebear.com/7.x/avataaars/svg?seed=Oscar',
  '/malelogo1.png',
  '/malelogo2.png',
  '/malelogo3.png',
  '/femalelogo1.png',
  '/femalelogo2.png',
  '/femalelogo3.png',
];

const NextGenEditProfileDrawer = ({ open, onClose, userData, onUpdateSuccess }) => {
  const { updateBuilder } = useNextGenAuth();
  const [formData, setFormData] = useState({
    fullName: '',
    mobileNumber: '',
    college: '',
    city: '',
    state: '',
    country: '',
    profileImage: ''
  });

  const [initialData, setInitialData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  useEffect(() => {
    if (userData && open) {
      const data = {
        fullName: userData.full_name || '',
        mobileNumber: userData.mobile_number || '',
        college: userData.college || '',
        city: userData.city || '',
        state: userData.state || '',
        country: userData.country || 'India',
        profileImage: userData.profile_image || ''
      };
      setFormData(data);
      setInitialData(data);
      setFieldErrors({});
      setError(null);
    }
  }, [userData, open]);

  const isDirty = JSON.stringify(formData) !== JSON.stringify(initialData);

  const handleCloseAttempt = () => {
    if (isDirty) {
      setShowDiscardDialog(true);
    } else {
      onClose();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const errors = {};
    if (!formData.fullName.trim()) errors.fullName = 'Full name is required';
    else if (formData.fullName.trim().length < 3) errors.fullName = 'Minimum 3 characters required';
    else if (formData.fullName.length > 100) errors.fullName = 'Maximum 100 characters allowed';

    if (!formData.mobileNumber.trim()) errors.mobileNumber = 'Mobile number is required';
    else if (!/^[6789]\d{9}$/.test(formData.mobileNumber.trim())) {
      errors.mobileNumber = 'Must be 10 digits starting with 6,7,8,9';
    }

    if (formData.college && formData.college.length > 200) errors.college = 'Maximum 200 characters';
    ['city', 'state', 'country'].forEach(f => {
      if (formData[f] && formData[f].length > 100) errors[f] = 'Maximum 100 characters';
    });

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, profileImage: '/user image.webp' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setError(null);

    try {
      const payload = { ...formData };
      
      const response = await axios.put(`${API_BASE_URL}/api/nextgen-builder/profile`, payload, {
        headers: { 'user-id': userData?.id || localStorage.getItem('nextgen_builder_id') }
      });

      if (response.data.success) {
        // Update global context so Header/Sidebar/avatar refresh immediately
        updateBuilder(response.data.data);
        onUpdateSuccess(response.data.message || 'Profile updated successfully');
        onClose();
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update profile';
      const serverErrors = err.response?.data?.errors;
      if (err.response?.status === 422 && serverErrors && !Array.isArray(serverErrors)) {
        setFieldErrors(prev => ({ ...prev, ...serverErrors }));
        setError('Please fix the highlighted fields');
      } else if (err.response?.status === 409) {
        if (msg.toLowerCase().includes('mobile')) setFieldErrors(prev => ({ ...prev, mobileNumber: 'Mobile number already exists.' }));
        else setError(msg);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleCloseAttempt}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 440 }, p: 0, borderLeft: 'none' }
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #E5E7EB' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0F172A' }}>Edit Profile</Typography>
            <IconButton onClick={handleCloseAttempt} size="small">
              <CloseRoundedIcon />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
            <form onSubmit={handleSubmit}>
              <Stack spacing={2.5}>
                {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

                {/* Profile Avatar Selection */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    src={formData.profileImage}
                    sx={{ width: 100, height: 100, border: '4px solid #F1F5F9', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
                  >
                    {formData.fullName?.[0]?.toUpperCase() || 'U'}
                  </Avatar>
                  
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, display: 'block', mb: 1, textAlign: 'center' }}>
                      CHOOSE AN AVATAR
                    </Typography>
                    <Grid container spacing={1} justifyContent="center">
                      {AVATARS.map((url, idx) => (
                        <Grid item key={idx}>
                          <Box
                            onClick={() => setFormData(prev => ({ ...prev, profileImage: url }))}
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              cursor: 'pointer',
                              border: '2px solid',
                              borderColor: formData.profileImage === url ? '#6C3BFF' : 'transparent',
                              p: 0.5,
                              transition: 'all 0.2s',
                              '&:hover': { transform: 'scale(1.1)' }
                            }}
                          >
                            <Avatar src={url} sx={{ width: '100%', height: '100%' }} />
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Box>

                <Divider sx={{ my: 0.5 }} />

                <TextField
                  label="Full Name"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!fieldErrors.fullName}
                  helperText={fieldErrors.fullName}
                  size="small"
                />

                <TextField
                  label="Username"
                  value={userData?.username || ''}
                  fullWidth
                  disabled
                  size="small"
                  helperText="Username cannot be changed"
                  InputProps={{
                    startAdornment: <Typography sx={{ color: '#94A3B8', mr: 0.5, fontSize: '0.875rem' }}>@</Typography>
                  }}
                />

                <TextField
                  label="Email Address"
                  value={userData?.email || ''}
                  fullWidth
                  disabled
                  size="small"
                  helperText="This email cannot be changed."
                />

                <TextField
                  label="Mobile Number"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!fieldErrors.mobileNumber}
                  helperText={fieldErrors.mobileNumber}
                  size="small"
                  inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                />

                <TextField
                  label="College"
                  name="college"
                  value={formData.college}
                  onChange={handleChange}
                  fullWidth
                  error={!!fieldErrors.college}
                  helperText={fieldErrors.college}
                  size="small"
                />


                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="City"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      fullWidth
                      error={!!fieldErrors.city}
                      helperText={fieldErrors.city}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="State"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      fullWidth
                      select
                      SelectProps={{ native: true }}
                      InputLabelProps={{ shrink: true }}
                      error={!!fieldErrors.state}
                      helperText={fieldErrors.state}
                      size="small"
                    >
                      <option value="">Select state</option>
                      {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      fullWidth
                      error={!!fieldErrors.country}
                      helperText={fieldErrors.country}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Stack>
            </form>
          </Box>

          {/* Footer */}
          <Box sx={{ p: 2, borderTop: '1px solid #E5E7EB', bgcolor: '#F8FAFC' }}>
            <Stack direction="row" spacing={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleCloseAttempt}
                disabled={loading}
                sx={{ borderRadius: '10px', fontWeight: 600, color: '#64748B', borderColor: '#CBD5E1' }}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                disabled={loading || !isDirty}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SaveRoundedIcon />}
                sx={{
                  borderRadius: '10px',
                  fontWeight: 600,
                  bgcolor: '#6C3BFF',
                  '&:hover': { bgcolor: '#5020E0' },
                  boxShadow: '0 4px 12px rgba(108,59,255,0.2)'
                }}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Drawer>

      {/* Discard Changes Dialog */}
      <Dialog
        open={showDiscardDialog}
        onClose={() => setShowDiscardDialog(false)}
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. Discard changes?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setShowDiscardDialog(false)} sx={{ color: '#64748B', fontWeight: 600 }}>
            Stay
          </Button>
          <Button
            onClick={() => {
              setShowDiscardDialog(false);
              onClose();
            }}
            variant="contained"
            color="error"
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NextGenEditProfileDrawer;
